import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Select } from '@/components/ui/Select'
import { useSimRuns, useSimScenarios } from '@/hooks/api/useSimulations'
import { useAssistantsSimple } from '@/hooks/api/useAssistants'
import { StatusBadge, PassFailBadge } from './StatusBadge'
import type { SimRun, SimRunFilters, SimRunStatus } from '@/types/simulation'
import type { SortDescriptor } from 'react-aria-components'

const STATUS_OPTIONS: SimRunStatus[] = ['pending', 'running', 'completed', 'failed']

/**
 * Runs tab - displays list of simulation runs with filters
 */
export function SimRunsTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Filters
  const [filters, setFilters] = useState<SimRunFilters>({})
  const [page, setPage] = useState(1)
  const perPage = 25

  // Sort (client-side for now)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'id',
    direction: 'descending',
  })

  // Fetch data
  const { data: runs = [], isLoading } = useSimRuns({ filters })
  const { data: scenarios = [] } = useSimScenarios({})
  const { data: assistants = [] } = useAssistantsSimple()

  // Sort runs client-side
  const sortedRuns = [...runs].sort((a, b) => {
    const column = sortDescriptor.column as keyof SimRun
    const aVal = a[column]
    const bVal = b[column]
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    return sortDescriptor.direction === 'descending' ? -cmp : cmp
  })

  // Paginate
  const totalRuns = sortedRuns.length
  const totalPages = Math.ceil(totalRuns / perPage)
  const paginatedRuns = sortedRuns.slice((page - 1) * perPage, page * perPage)

  // Handlers
  const handleRowClick = useCallback(
    (run: SimRun) => {
      navigate(`/simulations/runs/${run.id}`)
    },
    [navigate]
  )

  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  const handleStatusFilter = useCallback((key: string | number | null) => {
    setFilters((prev) => ({
      ...prev,
      status: key ? (key as SimRunStatus) : undefined,
    }))
    setPage(1)
  }, [])

  const handleScenarioFilter = useCallback((key: string | number | null) => {
    setFilters((prev) => ({
      ...prev,
      scenario_id: key ? Number(key) : undefined,
    }))
    setPage(1)
  }, [])

  const handleAssistantFilter = useCallback((key: string | number | null) => {
    setFilters((prev) => ({
      ...prev,
      assistant_id: key ? Number(key) : undefined,
    }))
    setPage(1)
  }, [])

  // Get display names
  const getScenarioName = (scenarioId: number) => {
    const scenario = scenarios.find((s) => s.id === scenarioId)
    return scenario?.name ?? t('resources.simulations.scenario_fallback', { id: scenarioId })
  }

  const getAssistantName = (assistantId: number) => {
    const assistant = assistants.find((a) => a.id === assistantId)
    return assistant?.name ?? t('resources.simulations.assistant_fallback', { id: assistantId })
  }

  // Status options for filter
  const statusOptions = [
    { value: '', label: t('resources.generic.all') },
    ...STATUS_OPTIONS.map((status) => ({
      value: status,
      label: t(`pages.simulations.statuses.${status}`),
    })),
  ]

  // Scenario options for filter
  const scenarioOptions = [
    { value: '', label: t('resources.generic.all') },
    ...scenarios.map((scenario) => ({
      value: String(scenario.id),
      label: scenario.name,
    })),
  ]

  // Assistant options for filter
  const assistantOptions = [
    { value: '', label: t('resources.generic.all') },
    ...assistants.map((assistant) => ({
      value: String(assistant.id),
      label: assistant.name,
    })),
  ]

  // Filter controls component
  const FiltersContent = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="w-44">
        <Select
          options={statusOptions}
          selectedKey={filters.status ?? ''}
          onSelectionChange={handleStatusFilter}
          placeholder={t('pages.simulations.filters.status')}
          aria-label={t('pages.simulations.filters.status')}
        />
      </div>
      <div className="w-44">
        <Select
          options={scenarioOptions}
          selectedKey={filters.scenario_id ? String(filters.scenario_id) : ''}
          onSelectionChange={handleScenarioFilter}
          placeholder={t('pages.simulations.filters.scenario')}
          aria-label={t('pages.simulations.filters.scenario')}
        />
      </div>
      <div className="w-44">
        <Select
          options={assistantOptions}
          selectedKey={filters.assistant_id ? String(filters.assistant_id) : ''}
          onSelectionChange={handleAssistantFilter}
          placeholder={t('pages.simulations.filters.assistant')}
          aria-label={t('pages.simulations.filters.assistant')}
        />
      </div>
    </div>
  )

  // Loading state
  if (isLoading) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.runs.title')}
          contentTrailing={FiltersContent}
        />
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      </TableCard.Root>
    )
  }

  // Empty state
  if (paginatedRuns.length === 0) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.runs.title')}
          contentTrailing={FiltersContent}
        />
        <div className="flex flex-1 items-center justify-center py-12 text-gray-500">
          {t('pages.simulations.runs.empty')}
        </div>
      </TableCard.Root>
    )
  }

  // Check if any runs are still in progress (for showing live indicator)
  const hasActiveRuns = runs.some(
    (run) => run.status === 'pending' || run.status === 'running'
  )

  return (
    <TableCard.Root size="sm" className="relative flex h-full flex-col">
      <TableCard.Header
        title={t('pages.simulations.runs.title')}
        contentTrailing={
          <div className="flex items-center gap-3">
            {/* Live indicator when runs are in progress */}
            {hasActiveRuns && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                <span>{t('pages.simulations.runs.live')}</span>
              </div>
            )}
            {FiltersContent}
          </div>
        }
      />

      <div className="flex-1 overflow-auto">
        <Table
          aria-label={t('pages.simulations.runs.title')}
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
        >
          <Table.Header>
            <Table.Head id="id" label={t('pages.simulations.runs.columns.id')} allowsSorting isRowHeader />
            <Table.Head id="scenario_id" label={t('pages.simulations.runs.columns.scenario')} />
            <Table.Head id="assistant_id" label={t('pages.simulations.runs.columns.assistant')} />
            <Table.Head id="status" label={t('pages.simulations.runs.columns.status')} />
            <Table.Head id="overall_passed" label={t('pages.simulations.runs.columns.result')} />
            <Table.Head id="duration_seconds" label={t('pages.simulations.runs.columns.duration')} allowsSorting />
          </Table.Header>

          <AriaTableBody>
            {paginatedRuns.map((run) => (
              <Table.Row
                key={run.id}
                className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onAction={() => handleRowClick(run)}
              >
                <Table.Cell className="font-medium">#{run.id}</Table.Cell>
                <Table.Cell>{getScenarioName(run.scenario_id)}</Table.Cell>
                <Table.Cell>{getAssistantName(run.assistant_id)}</Table.Cell>
                <Table.Cell>
                  <StatusBadge status={run.status} />
                </Table.Cell>
                <Table.Cell>
                  <PassFailBadge passed={run.overall_passed} />
                </Table.Cell>
                <Table.Cell>
                  {run.duration_seconds != null
                    ? `${run.duration_seconds.toFixed(1)}s`
                    : '-'}
                </Table.Cell>
              </Table.Row>
            ))}
          </AriaTableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          perPage={perPage}
          totalCount={totalRuns}
          onPageChange={setPage}
        />
      )}
    </TableCard.Root>
  )
}

export default SimRunsTab
